import { FormTypes } from '@oneblink/types'
import { findElement, replaceElement } from '../../definition-manipulation'

type ChargeableItem = {
  label: string
  charge: number
  total: number
}

type FacilityItem = {
  facilityName: string
  facilityType: string
  price: number
  gstApplied: boolean
}

function getDefaultName(facilityType: string) {
  switch (facilityType) {
    case 'fixed':
      return 'Unnamed Facility'
    case 'vehicle':
      return 'Unnamed Vehicle'
    case 'fishing':
      return 'Fishing Business - No address provided'
    case 'beach hauling':
      return 'Unnamed Beach/Estuary'
    case 'vessel':
      return 'Unnamed Vessel'
    default:
      return 'Unnamed Facility'
  }
}

function licenceChargesTable({
  chargeableItems,
  facilities,
  payableAmount,
  applicationFee,
}: {
  chargeableItems: ChargeableItem[]
  facilities?: FacilityItem[]
  payableAmount?: number
  applicationFee?: number
}) {
  if (!chargeableItems.length && !facilities) {
    return `
      <b>
        <i>No payment is required for this application - please proceed to submission</i>
      </b>
      `
  }

  if (payableAmount === undefined) {
    return ''
  }

  let tableHtml = `
  <div class="fa-charges-table__container">
    <table class="fa-charges-table">
      <tr class="fa-charges-table__header">
        <td>Chargeable items</td>
        <td>Charges</td>
      </tr>`

  if (Array.isArray(facilities)) {
    tableHtml += facilities.reduce((html, facility) => {
      html += `
      <tr class="fa-charges-table__row">
        <td class="fa-charges-table__row-label">${
          facility.facilityName ||
          `<i>${getDefaultName(facility.facilityType)}</i>`
          //Display GST only for non zero items
        }${facility.gstApplied && !!facility.price ? '*' : ''}</td>
        <td class="fa-charges-table__row-value">$${facility.price.toFixed(
          2,
        )}</td>
      </tr>
    `
      return html
    }, '')

    const facilityTotal = facilities
      .reduce((total, { price }) => (total += price), 0)
      .toFixed(2)

    tableHtml += `
      <tr class="fa-charges-table__header fa-charges-table__facility-total-row">
        <td class="fa-charges-table__row-label">Facility total charges</td>
        <td class="fa-charges-table__row-value">$${facilityTotal}</td>
      </tr>
    `
  }

  if (applicationFee !== undefined && !Number.isNaN(applicationFee)) {
    tableHtml += `
    <tr class="fa-charges-table__row">
      <td class="fa-charges-table__row-label">Application fee</td>
      <td class="fa-charges-table__row-value">$${applicationFee.toFixed(2)}</td>
    </tr>
    `
  }

  if (chargeableItems.length) {
    tableHtml += `
    <tr class="fa-charges-table__header fa-charges-table__order-items-row">
      <td>Order items</td>
      <td>&nbsp;</td>
    </tr>
    `
  }

  tableHtml += chargeableItems.reduce((itemRows, item) => {
    if (item.charge >= 0) {
      if (item.total > 1) {
        itemRows += `
          <tr class="fa-charges-table__row">
            <td class="fa-charges-table__row-label">
              ${item.label} x${item.total}
            </td>
            <td class="fa-charges-table__row-value">
              $${(item.charge * item.total).toFixed(2)}
            </td>
          </tr>
        `
      } else {
        itemRows += `
          <tr class="fa-charges-table__row">
            <td class="fa-charges-table__row-label">${item.label}</td>
            <td class="fa-charges-table__row-value">$${item.charge.toFixed(
              2,
            )}</td>
          </tr>
        `
      }
    }
    return itemRows
  }, '')

  tableHtml += `
    <tr class="fa-charges-table__row fa-charges-table__total-payable-row">
      <td class="fa-charges-table__row-label">TOTAL PAYABLE NOW</td>
      <td class="fa-charges-table__row-value">$${payableAmount.toFixed(2)}</td>
    </tr>
  </table>
  <p class="fa-charges-table__footer">Items marked with * include GST.</p>
  `

  return tableHtml
}

function getProduct(products: Product[], productName: string) {
  if (!products) return undefined

  return products.find(
    (product) => product?.Product_Name.toLowerCase() === productName,
  )
}

function getChargeableItems(submission: Record<string, unknown>) {
  const chargeableItems = []
  const products = submission.products__hidden as Product[]

  const asicCompanySearch = getProduct(products, 'company extract search')
  const eggStampAndInk = getProduct(products, 'egg stamp and ink')
  const foodSafetyPack = getProduct(products, 'food safety pack')
  const codeOfPracticeRecordBook = getProduct(
    products,
    'code of practice record book',
  )
  const nswColdStoreFoodSafetyProgram = getProduct(
    products,
    'nsw cold store food safety program',
  )

  if (asicCompanySearch && asicCompanySearch.total > 0) {
    chargeableItems.push({
      label: 'ASIC Company Search*',
      charge: asicCompanySearch.price,
      total: asicCompanySearch.total,
    })
  }

  if (eggStampAndInk && eggStampAndInk.total > 0) {
    chargeableItems.push({
      label: 'Egg stamp and Ink*',
      charge: eggStampAndInk.price,
      total: eggStampAndInk.total,
    })
  }

  if (foodSafetyPack && foodSafetyPack.total > 0) {
    chargeableItems.push({
      label: 'Food Safety Pack*',
      charge: foodSafetyPack.price,
      total: foodSafetyPack.total,
    })
  }

  if (codeOfPracticeRecordBook && codeOfPracticeRecordBook.total > 0) {
    chargeableItems.push({
      label: 'Code of Practice Record book*',
      charge: codeOfPracticeRecordBook.price,
      total: codeOfPracticeRecordBook.total,
    })
  }

  if (
    nswColdStoreFoodSafetyProgram &&
    nswColdStoreFoodSafetyProgram.total > 0
  ) {
    chargeableItems.push({
      label: 'Cold Store Food Safety Program*',
      charge: nswColdStoreFoodSafetyProgram.price,
      total: nswColdStoreFoodSafetyProgram.total,
    })
  }

  return chargeableItems as ChargeableItem[]
}

export function generateLicenceChargesTable(
  submission: Record<string, unknown>,
  definition: FormTypes.Form,
) {
  const chargeableItems = getChargeableItems(submission)

  // Charges payable now table logic
  const existingChargesPayableNowElement = findElement<FormTypes.HtmlElement>(
    definition,
    'licenceChargesPayableNow',
  )

  if (existingChargesPayableNowElement) {
    const facilities = submission.facilities__hidden as
      | FacilityItem[]
      | undefined

    const chargesPayableTable = licenceChargesTable({
      chargeableItems,
      facilities,
      payableAmount: submission.paymentTotal__hidden as number,
      applicationFee: submission.applicationFee__hidden as number,
    })

    const chargesPayableNowElement = {
      ...existingChargesPayableNowElement.value,
      defaultValue: chargesPayableTable,
    }

    existingChargesPayableNowElement.parent.elements = replaceElement(
      existingChargesPayableNowElement.parent.elements || [],
      existingChargesPayableNowElement.value,
      chargesPayableNowElement,
    )
  } else {
    console.warn(
      `Unable to display charges payable now table as 'licenceChargesPayableNow' element does not exist`,
    )
  }

  return definition
}
